/**
 * MMODroppedRelic - Display class for relics dropped on the map.
 * Uses tier-specific sprite images with glow and pulsing animation.
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord } from '../../shared/HexUtils';

export interface DroppedRelicData {
  id: string;
  technologyId: number;
  name: string;
  rarity: string;
  tier: number;
  category: string;
  weight: number;
  position: HexCoord;
}

const TIER_SPRITE_MAP: Record<number, string> = {
  1: 'relic-t1',
  2: 'relic-t2',
  3: 'relic-t3',
  4: 'relic-t4',
  5: 'relic-t5',
};

const TIER_COLORS: Record<number, number> = {
  1: 0xffffff,
  2: 0x44ff44,
  3: 0x4488ff,
  4: 0xaa44ff,
  5: 0xffcc00,
};

const RARITY_GLOW: Record<string, number> = {
  common: 0x888888,
  uncommon: 0x44cc44,
  rare: 0x4488ff,
  epic: 0xaa44ff,
  legendary: 0xffcc00,
};

// Sprite display sizes scale up with tier
const TIER_SIZES: Record<number, number> = {
  1: 24,
  2: 28,
  3: 30,
  4: 34,
  5: 38,
};

export class MMODroppedRelic extends Phaser.GameObjects.Container {
  public id: string;
  public hexCoord: HexCoord;
  public relicName: string;
  public rarity: string;
  public tier: number;
  public category: string;
  public weight: number;
  public technologyId: number;
  public entityType = 'dropped_relic';

  private relicSprite: Phaser.GameObjects.Image | null = null;
  private glowGraphics: Phaser.GameObjects.Graphics;
  private entityGraphics: Phaser.GameObjects.Graphics;
  private selectionIndicator: Phaser.GameObjects.Graphics;
  private nameLabel: Phaser.GameObjects.Text;
  private glowTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, data: DroppedRelicData) {
    const pixel = axialToPixel(data.position.q, data.position.r);
    super(scene, pixel.x, pixel.y);

    this.id = data.id;
    this.hexCoord = data.position;
    this.relicName = data.name;
    this.rarity = data.rarity || 'common';
    this.tier = data.tier || 1;
    this.category = data.category || 'special';
    this.weight = data.weight || 10;
    this.technologyId = data.technologyId;

    this.selectionIndicator = this.scene.add.graphics();
    this.selectionIndicator.lineStyle(2, 0xffff00, 0.8);
    this.selectionIndicator.strokeCircle(0, 0, 16);
    this.selectionIndicator.setVisible(false);
    this.add(this.selectionIndicator);

    this.glowGraphics = this.scene.add.graphics();
    this.add(this.glowGraphics);

    this.entityGraphics = this.scene.add.graphics();
    this.add(this.entityGraphics);

    const tierColor = '#' + (TIER_COLORS[this.tier] || 0xffffff).toString(16).padStart(6, '0');
    this.nameLabel = this.scene.add.text(0, 18, `T${this.tier} Relic`, {
      fontSize: '7px',
      color: tierColor,
      fontFamily: "'Share Tech Mono', monospace",
    });
    this.nameLabel.setOrigin(0.5);
    this.nameLabel.setVisible(false);
    this.add(this.nameLabel);

    this.drawVisuals();
    this.setDepth(5);

    // Expand hit-zone to 48px minimum on touch devices (iOS/Android guidelines)
    const _isCoarseRelic = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(pointer: coarse)').matches;
    const _relicHit = _isCoarseRelic ? 48 : 36;
    this.setSize(_relicHit, _relicHit);
    this.setInteractive(
      new Phaser.Geom.Rectangle(-_relicHit / 2, -_relicHit / 2, _relicHit, _relicHit),
      Phaser.Geom.Rectangle.Contains,
    );

    // NOTE: No sprite-level 'pointerdown' handler — see MMOUnit.ts for rationale.
    // Selection is handled in MMOGameScene.onPointerUp via getEntityAtWorldPoint().

    this.glowTween = this.scene.tweens.add({
      targets: this.glowGraphics,
      alpha: { from: 0.3, to: 0.8 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  public updateFromServer(data: DroppedRelicData): void {
    if (!this.scene || !this.scene.sys) return;

    this.relicName = data.name;
    this.rarity = data.rarity || this.rarity;
    this.tier = data.tier || this.tier;
    this.weight = data.weight || this.weight;

    const newPos = data.position;
    if (newPos.q !== this.hexCoord.q || newPos.r !== this.hexCoord.r) {
      this.hexCoord = newPos;
      const pixel = axialToPixel(newPos.q, newPos.r);
      this.setPosition(pixel.x, pixel.y);
    }
  }

  private drawVisuals(): void {
    const glowColor = RARITY_GLOW[this.rarity] || 0x888888;

    // Glow circle behind sprite
    this.glowGraphics.clear();
    this.glowGraphics.fillStyle(glowColor, 0.15);
    this.glowGraphics.fillCircle(0, 0, 14);
    this.glowGraphics.fillStyle(glowColor, 0.08);
    this.glowGraphics.fillCircle(0, 0, 20);

    // Try sprite first
    const spriteKey = TIER_SPRITE_MAP[this.tier];
    if (spriteKey && this.scene.textures.exists(spriteKey)) {
      this.entityGraphics.clear();
      this.entityGraphics.setVisible(false);

      const size = TIER_SIZES[this.tier] || 28;
      if (!this.relicSprite) {
        this.relicSprite = this.scene.add.image(0, 0, spriteKey);
        this.relicSprite.setDisplaySize(size, size);
        this.add(this.relicSprite);
        this.moveTo(this.relicSprite, 2);
      } else if (this.relicSprite.texture.key !== spriteKey) {
        this.relicSprite.setTexture(spriteKey);
        this.relicSprite.setDisplaySize(size, size);
      }
    } else {
      this.drawFallbackCrystal();
    }
  }

  private drawFallbackCrystal(): void {
    const color = TIER_COLORS[this.tier] || 0xffffff;

    this.entityGraphics.clear();
    this.entityGraphics.setVisible(true);
    this.entityGraphics.fillStyle(color, 0.9);
    this.entityGraphics.beginPath();
    this.entityGraphics.moveTo(0, -10);
    this.entityGraphics.lineTo(7, 0);
    this.entityGraphics.lineTo(0, 10);
    this.entityGraphics.lineTo(-7, 0);
    this.entityGraphics.closePath();
    this.entityGraphics.fillPath();

    this.entityGraphics.lineStyle(1.5, color, 1);
    this.entityGraphics.beginPath();
    this.entityGraphics.moveTo(0, -10);
    this.entityGraphics.lineTo(7, 0);
    this.entityGraphics.lineTo(0, 10);
    this.entityGraphics.lineTo(-7, 0);
    this.entityGraphics.closePath();
    this.entityGraphics.strokePath();

    this.entityGraphics.lineStyle(0.5, 0xffffff, 0.3);
    this.entityGraphics.lineBetween(0, -10, 0, 10);
    this.entityGraphics.lineBetween(-7, 0, 7, 0);
  }

  public setSelected(selected: boolean): void {
    this.selectionIndicator.setVisible(selected);
  }

  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.3);
    } else {
      this.setAlpha(1);
    }
  }

  public destroy(fromScene?: boolean): void {
    if (this.glowTween) {
      this.glowTween.destroy();
      this.glowTween = null;
    }
    if (this.relicSprite) {
      this.relicSprite.destroy();
      this.relicSprite = null;
    }
    super.destroy(fromScene);
  }
}
